<template>
  <div
    data-cy="app-nav"
    :class="[
      appStore.mainBanner.show === true ? 'tw:lg:fixed tw:lg:top-[85px]' : null,
    ]"
    class="app-nav-need-help">
    <div v-if="!!userProfile"
      class="d-flex justify-content-center position-relative h-100 me-3"
      @click="setDropdown(true)"
      v-click-outside="() => setDropdown(false)">
      <div data-cy="app-nav-dropdown" class="app-nav-dropdown">
        <!-- Mobile -->
        <a v-if="contactInfoEnabled && !hideContactInfo"
          :href="cxNumberLink"
          class="d-sm-block d-lg-none d-none call-us me-4 pe-4 type-500">
          Need help? <br/>{{ cxNumber }}
        </a>
        <!-- .stop: the wrapper's @click opens the profile dropdown, which a tap on the slotted chip must not do -->
        <div class="d-lg-none" @click.stop>
          <slot name="contact-chip" :desktop="false" />
        </div>
        <div :class="{ 'full-nav': full }" class="d-lg-none marketplace-nav-link m-0 ms-2 text-truncate">
          <font-awesome-icon :icon="faCircleUser"></font-awesome-icon>
          <span data-cy="mobile-username" class="username text-capitalize p-sm fw-bold ms-2">{{ name }}</span>
        </div>
        <!-- Desktop -->
        <div v-if="contactInfoEnabled && !hideContactInfo" class="call-us d-none d-lg-block me-4 pe-4 type-500">
          Need help? Call {{ cxNumber }}
        </div>
        <div class="d-none d-lg-block" @click.stop>
          <slot name="contact-chip" :desktop="true" />
        </div>
        <div
          :class="{ 'full-nav': full }"
          class="d-none d-lg-flex marketplace-nav-link desktop-profile-link text-truncate align-items-center bg-white p-1">
          <div class="desktop-icon-gray-background d-flex align-items-center justify-content-center">
            <font-awesome-icon :icon="faCircleUser"></font-awesome-icon>
          </div>
          <span data-cy="desktop-username" class="username text-capitalize text-truncate p-sm fw-bold ms-2 me-5">
            {{ name }}
          </span>
        </div>
      </div>
      <NavMenu v-show="dropdownVisible"
        :position="{
          position: 'absolute',
          top: '55px',
          right: '0px'
        }"
        @close="setDropdown(false)" />
    </div>
    <div v-else-if="!isGuestPage" class="app-nav-dropdown no-user h-100">
      <!-- Mobile -->
      <a v-if="contactInfoEnabled"
        :href=cxNumberLink
        class="d-md-none d-sm-block call-us me-3 pe-3 type-500">
        Need help? <br>{{ cxNumber }}
      </a>
      <!-- Desktop -->
      <p v-if="contactInfoEnabled" class="call-us d-none d-md-block me-4 pe-4 mb-0 type-500">
        Need help? Call {{ cxNumber }}
      </p>
      <a data-cy="sign-in"
        class="sign-in me-3"
        :class="{'customized-affiliate': !!affiliateCustomizations}"
        href="javascript:;"
        @click="login()">
        Sign In
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { phone } from '~/libs/format'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useBorrowerStore } from '~/store/borrower'
import { useRootStore } from '~/store/root'
import env from '~/libs/env'
import { get } from 'es-toolkit/compat'
import NavMenu from '~/components/NavMenu.vue'
import { useAppStore } from '~/store/app'
import { faCircleUser } from '@fortawesome/pro-regular-svg-icons'

const appStore = useAppStore()

defineProps({
  full: {
    type: Boolean,
    default: false,
  },
  hideContactInfo: {
    type: Boolean,
    default: false,
  },
})

const route = useRoute()
const borrowerStore = useBorrowerStore()
const rootStore = useRootStore()
const affiliateCustomizationStore = useAffiliateCustomizationStore()

const { userProfile } = storeToRefs(rootStore)
const {
  affiliateCustomizations,
  contactInfoEnabled,
  contactInfoPhone,
} = storeToRefs(affiliateCustomizationStore)

const dropdownVisible = ref(false)

const borrowerName = computed(() => get(borrowerStore, 'borrower.name'))

const cxNumber = computed(() => phone(contactInfoPhone.value ?? env('customerServicePhoneNumber')))
const cxNumberLink = computed(() => 'tel:' + (contactInfoPhone.value ?? env('customerServicePhoneNumber')))

const name = computed(() => {
  if (borrowerName.value) {
    return borrowerName.value
  }
  if (userProfile.value?.first && userProfile.value?.last) {
    return userProfile.value.first.length + userProfile.value.last.length + 1 <= 16
      ? userProfile.value.first + ' ' + userProfile.value.last
      : userProfile.value.first
  }
  return ''
})

const isGuestPage = computed(() =>
  route.path.startsWith('/guest/') || route.path.includes('document-upload-request/contact-guest')
)

watch(() => route.path, () => {
  dropdownVisible.value = false
})

function login() {
  redirectToWidget()
}

function setDropdown(val) {
  if (val !== dropdownVisible.value) {
    dropdownVisible.value = val
  }
}
</script>

<style lang="scss" scoped>
$app-primary-color: var(--app-primary-color);

.sign-in .customized-affiliate {
  color: $app-primary-color;
}

.call-us {
  border-right: 1px solid $border-300;
  font-size: 14px;
  @include media-breakpoint-down(sm) {
      border: none;
  }
}

.hide-sign-in > .call-us,
.hide-sign-in ~ .call-us {
  border-right: none;
  margin-right: 0 !important;
  padding-right: 0 !important;
}

.marketplace-nav-link {
  color: $type-600;
  cursor: pointer;
}

.app-nav-dropdown {
  max-width: 464px;
  display: flex;
  align-items: center;
  z-index: 1005;
  white-space: nowrap;
  vertical-align: middle;
}

@include media-breakpoint-down(sm) {
  .app-nav-dropdown:not(.is-modern):not(.no-user) {
    max-width: 150px;
  }
}

@media (max-width: 400px) {
  .no-user {
    .call-us {
      display: none !important;
    }
  }
}

.desktop-profile-link {
  border-radius: 40px;
  border: 1px solid #DADFE3;
}

.desktop-icon-gray-background {
  height: 40px;
  width: 40px;
  min-width: 40px;
  min-height: 40px;
  border-radius: 20px;
  background: $disabled-background;
}

.username {
  text-transform: uppercase;
  letter-spacing: .5px;
}

.full-nav > :not(.username, .desktop-icon-gray-background) {
  font-size: 14px;
  &:hover {
    color: #0091FF;
  }
}
</style>
